import React, { useEffect, useState } from "react";
import FetchAllGatherings from "../../api/FetchAllGatherings";
import { fetchAllAffiliateRequests } from "../../api/FetchAllAffiliateRequests";
import {
  approveAffiliateRequest,
  deleteAffiliateRequest,
} from "../../api/ApproveOrDeleteAffiliateRequest";
import FetchAllVendors from "../../api/FetchAllVendors";
import { useNavigate } from "react-router-dom";
import "../../styles/views/admin-view.css";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import { auth, firestore, waitForAuthUser } from "../../config/FirebaseConfig";
import { doc, getDoc } from "firebase/firestore";

const AdminView = () => {
  const [gatherings, setGatherings] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [artists, setArtists] = useState([]);
  const [affiliateRequests, setAffiliateRequests] = useState([]);
  const [activeTab, setActiveTab] = useState("gatherings");
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const verifyAdminAccess = async () => {
      const currentUser = auth.currentUser || (await waitForAuthUser());

      if (!currentUser) {
        setIsAuthorized(false);
        navigate("/", { replace: true });
        return;
      }

      const userDoc = await getDoc(doc(firestore, "Users", currentUser.uid));
      const hasAdminAccess = userDoc.exists() && userDoc.data()?.admin === true;

      setIsAuthorized(hasAdminAccess);

      if (!hasAdminAccess) {
        navigate("/", { replace: true });
      }
    };

    verifyAdminAccess();
  }, [navigate]);

  useEffect(() => {
    if (isAuthorized !== true) {
      return;
    }

    const fetchAllData = async () => {
      setLoading(true);
      try {
        const [adminGatherings, allVendors, allArtists, affiliateReqs] = await Promise.all([
          FetchAllGatherings(),
          FetchAllVendors("Vendor"),
          FetchAllVendors("Artist"), // Assuming Artists use the same API with different type
          fetchAllAffiliateRequests(),
        ]);

        setGatherings(adminGatherings || []);
        setVendors(allVendors || []);
        setArtists(allArtists || []);
        setAffiliateRequests(affiliateReqs || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [isAuthorized]);

  if (isAuthorized === null || isAuthorized === false) {
    return <LoadingScreen />;
  }

  const handleApprove = async (id) => {
    try {
      const result = await approveAffiliateRequest(id);
      setAffiliateRequests((prev) =>
        prev.map((req) => (req.id === id ? { ...req, ...result } : req))
      );
    } catch (error) {
      console.error("Error approving affiliate request:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this request?")) {
      try {
        await deleteAffiliateRequest(id);
        setAffiliateRequests((prev) => prev.filter((req) => req.id !== id));
      } catch (error) {
        console.error("Error deleting affiliate request:", error);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric", 
      year: "numeric" 
    });
  };

  const TabButton = ({ tabKey, label, count }) => (
    <button
      className={`tab-button ${activeTab === tabKey ? "active" : ""}`}
      onClick={() => setActiveTab(tabKey)}
    >
      {label}
      <span className="tab-count">{count}</span>
    </button>
  );

  if (loading) {
    return (
      <div className="admin-view-container">
        <LoadingScreen />
      </div>
    );
  }

  return (
    <div className="admin-view-container">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <p className="admin-subtitle">Manage all gatherings, vendors, artists, and affiliate requests</p>
      </div>

      <div className="tabs-container">
        <div className="tabs-header">
          <TabButton tabKey="gatherings" label="Gatherings" count={gatherings.length} />
          <TabButton tabKey="vendors" label="Vendors" count={vendors.length} />
          <TabButton tabKey="artists" label="Artists" count={artists.length} />
          <TabButton tabKey="affiliates" label="Affiliate Requests" count={affiliateRequests.length} />
        </div>

        <div className="tab-content">
          {activeTab === "gatherings" && (
            <div className="table-section">
              {gatherings.length > 0 ? (
                <div className="table-wrapper">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Gathering Name</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Host Name</th>
                        <th>Location</th>
                        <th>Host Email</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gatherings.map((gathering) => (
                        <tr key={gathering.id}>
                          <td className="primary-cell">
                            <span className="link-text" onClick={() => navigate(`/dashboard/${gathering.id}`)}>
                              {gathering.gatheringName}
                            </span>
                          </td>
                          <td>{formatDate(gathering.startDate)}</td>
                          <td>{formatDate(gathering.endDate)}</td>
                          <td>{gathering.hostName}</td>
                          <td>{gathering.location}</td>
                          <td className="email-cell">{gathering.hostEmail}</td>
                          <td>
                            <button 
                              className="action-button view-button"
                              onClick={() => navigate(`/dashboard/${gathering.id}`)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No gatherings found</p>
                </div>
              )}
            </div>
          )}

          {activeTab === "vendors" && (
            <div className="table-section">
              {vendors.length > 0 ? (
                <div className="table-wrapper">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Vendor Name</th>
                        <th>Email</th>
                        <th>Website</th>
                        <th>Phone</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendors.map((vendor) => (
                        <tr key={vendor.id}>
                          <td className="primary-cell">{vendor.name}</td>
                          <td className="email-cell">{vendor.email}</td>
                          <td>
                            {vendor.website ? (
                              <a href={vendor.website} target="_blank" rel="noopener noreferrer" className="link-text">
                                {vendor.website}
                              </a>
                            ) : "-"}
                          </td>
                          <td>{vendor.phone || "-"}</td>
                          <td>
                            <button
                              className="action-button view-button"
                              onClick={() => navigate(`/vendor-dashboard/${vendor.id}`)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No vendors found</p>
                </div>
              )}
            </div>
          )}

          {activeTab === "artists" && (
            <div className="table-section">
              {artists.length > 0 ? (
                <div className="table-wrapper">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Artist Name</th>
                        <th>Email</th>
                        <th>Website</th>
                        <th>Genre</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {artists.map((artist) => (
                        <tr key={artist.id}>
                          <td className="primary-cell">{artist.name}</td>
                          <td className="email-cell">{artist.email}</td>
                          <td>
                            {artist.website ? (
                              <a href={artist.website} target="_blank" rel="noopener noreferrer" className="link-text">
                                {artist.website}
                              </a>
                            ) : "-"}
                          </td>
                          <td>{artist.genre || "-"}</td>
                          <td>
                            <button
                              className="action-button view-button"
                              onClick={() => navigate(`/artist-dashboard/${artist.id}`)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No artists found</p>
                </div>
              )}
            </div>
          )}

          {activeTab === "affiliates" && (
            <div className="table-section">
              {affiliateRequests.length > 0 ? (
                <div className="table-wrapper">
                  <table className="admin-table affiliates-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Social Link</th>
                        <th className="message-header">Message</th>
                        <th>Venmo</th>
                        <th>Status</th>
                        <th>Approved By</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {affiliateRequests.map((req) => (
                        <tr key={req.id}>
                          <td className="primary-cell">{req.name}</td>
                          <td>{req.email}</td>
                          <td>
                            {req.socialLink1 ? (
                              <a href={req.socialLink1} target="_blank" rel="noopener noreferrer" className="link-text">
                                View
                              </a>
                            ) : "-"}
                          </td>
                          <td className="message-cell">
                            <div className="message-content">
                              {req.message && req.message.length > 40 ? (
                                <>
                                  {req.message.substring(0, 40)}...
                                  <div className="message-tooltip">{req.message}</div>
                                </>
                              ) : (
                                req.message || "-"
                              )}
                            </div>
                          </td>
                          <td>{req.venmo || "-"}</td>
                          <td>
                            <span className={`status-badge ${req.approved ? "approved" : "pending"}`}>
                              {req.approved ? "Approved" : "Pending"}
                            </span>
                          </td>
                          <td>{req.approvedBy || "-"}</td>
                          <td>
                            <div className="action-buttons">
                              {!req.approved && (
                                <button 
                                  className="action-button approve-button"
                                  onClick={() => handleApprove(req.id)}
                                >
                                  Approve
                                </button>
                              )}
                              <button 
                                className="action-button delete-button"
                                onClick={() => handleDelete(req.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No affiliate requests found</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminView;